import { useEffect, useState } from "react";

import {
  Button,
  Callout,
  Card,
  Colors,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core";

import { validateEmail } from "@/components/helpers/validation/validate-email";
import { Box, Flex } from "@/components/layout/flexbox";
import { AttendeeList } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeList";
import { InvitedBoxStyle } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendeeManageSection/ManageAttendeesDialog";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { FullMeetingDocument, useSaveMeetingGuestsMutation } from "@/graphql";
import { Attendee, FullMeeting } from "@/types/meetings";

interface ManageGuestsDialogProps {
  meeting: FullMeeting;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  emails: string[];
}

export function ManageGuestsDialog({
  meeting,
  open,
  onClose,
  onSuccess,
  emails = [],
}: ManageGuestsDialogProps) {
  const [result, setResult] = useState({
    success: true,
    errorMsg: "",
  });
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [attendeeError, setAttendeeError] = useState<React.ReactNode>("");
  const [emailInput, setEmailInput] = useState("");

  const [saveMeetingGuests, { loading: saving }] = useSaveMeetingGuestsMutation(
    {
      onCompleted: (data) => {
        if (data?.saveMeetingGuests) {
          onClose();
          onSuccess();
        } else {
          setResult({
            success: false,
            errorMsg: "Error saving guest list",
          });
        }
      },
      onError: (error) => {
        setResult({
          success: false,
          errorMsg: error.message,
        });
      },
      refetchQueries: [
        {
          query: FullMeetingDocument,
          variables: { id: meeting?.id },
        },
      ],
      awaitRefetchQueries: true,
    },
  );

  useEffect(() => {
    if (open && emails) {
      const attendees: Attendee[] = emails.map((email) => ({
        id: "",
        email,
        user: {
          id: "",
          email,
        },
      }));
      setAttendees(attendees);
    }
  }, [emails, open]);

  const onRemoveInvitedAttendee = (attendee: Attendee) => {
    setAttendees((attendees) =>
      attendees.filter((a) => a?.user?.email !== attendee?.user?.email),
    );
  };

  const updateGuests = () => {
    const isEmail = validateEmail(emailInput);
    const listEmails = isEmail ? [...emails, emailInput] : emails;
    const distinctEmails = [...new Set(listEmails)];
    saveMeetingGuests({
      variables: {
        meetingId: meeting?.id,
        guests: distinctEmails,
      },
    });
  };

  const handleAddEmail = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(emailInput)) {
      setAttendeeError(
        <Box color={Colors.ORANGE2} mt={2}>
          Please enter a valid email address.
        </Box>,
      );
      return;
    }

    const attendeeIndex = attendees.findIndex(
      (a) => a?.user?.email === emailInput,
    );

    if (attendeeIndex === -1) {
      const newAttendee: Attendee = {
        id: "",
        email: emailInput,
        user: {
          id: "",
          email: emailInput,
        },
      };
      setAttendees([...attendees, newAttendee]);
      setAttendeeError("");
      setEmailInput("");
    } else {
      setAttendeeError(
        <Box color={Colors.ORANGE2} mt={2}>
          Email already exists.
        </Box>,
      );
    }
  };

  const buttonText = "Save";
  const title = "Manage Guests";

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box fontSize={18}>{title}</Box>}
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult({
          success: true,
          errorMsg: "",
        });
      }}
    >
      <Box p={3}>
        <Callout intent="primary" icon={null}>
          <Flex>
            <Box mr={2}>💡</Box>
            <Box>
              <Box fontWeight={700}>
                Add guests to this meeting's calendar invite
              </Box>
              <Box>
                Guests will be able to join the meeting but won't have Exec
                platform access to it.
              </Box>
            </Box>
          </Flex>
        </Callout>
        <FormGroup
          style={{ marginBottom: 16, marginTop: 20 }}
          label={<b>Guest Email</b>}
        >
          <InputGroup
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Enter email address"
            type="email"
            large={true}
            autoFocus={true}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddEmail(e);
              }
            }}
          />
          {attendeeError}
          {attendees && attendees.length > 0 && (
            <Card style={InvitedBoxStyle}>
              <AttendeeList
                attendees={attendees}
                onClick={onRemoveInvitedAttendee}
                mode="edit"
              />
            </Card>
          )}
        </FormGroup>
        {!result?.success && (
          <Callout icon={null} intent="danger">
            {result?.errorMsg}
          </Callout>
        )}
        <Flex mt={3} justifyContent="space-between">
          <Button
            large={true}
            loading={saving}
            intent="primary"
            onClick={() => updateGuests()}
          >
            {buttonText}
          </Button>
        </Flex>
      </Box>
    </StyledDialog>
  );
}
